import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { fontSize, margins } from "style";
import { useOrganization } from "hooks";
import { Toggle } from "components/Form";
import DisplayBox from "components/DisplayBox";
import { TextLoader } from "components/Loader";
import { FEATURE_ENTITLEMENTS } from "constants/index";

/**
 * OrganizationFeatureEntitlements
 *
 * The portal features every client user in this organization gets. This is the only place
 * they are decided — there is no per-user override, so turning one off here removes it for
 * everyone in the organization.
 *
 * @param {String} id - Organization ID
 */
const OrganizationFeatureEntitlements = ({ id, ...props }) => {
  const { data, loading, toggleFeatureEntitlement, settingFeatureEntitlements } = useOrganization({ id });
  const isLoading = loading || settingFeatureEntitlements;
  const granted = data?.featureEntitlements || [];
  const available = data?.availableFeatureEntitlements || [];

  return (
    <DisplayBox
      title={<FormattedMessage id="Organization.OrganizationFeatureEntitlements.Title" />}
      description={<FormattedMessage id="Organization.OrganizationFeatureEntitlements.Description" />}
      numColumns={1}
      marginSize={margins.normal}
      data={FEATURE_ENTITLEMENTS.filter(({ id: key }) => available.includes(key))}
      loading={isLoading}
      isEmpty={!isLoading && !available.length}
      emptyMsg={<FormattedMessage id="Organization.OrganizationFeatureEntitlements.EmptyMsg" />}
      itemRenderer={({ id: key, labelId }, index, itemLoading) => {
        const isEnabled = granted.includes(key);

        return itemLoading ? (
          <TextLoader />
        ) : (
          <div css={styles.container}>
            <span css={styles.info}>
              <FormattedMessage id={labelId} />
            </span>
            <Toggle name={key} value={isEnabled} onChange={() => toggleFeatureEntitlement(key, !isEnabled)} />
          </div>
        );
      }}
      {...props}
    />
  );
};

const styles = {
  info: css`
    max-width: 70%;
    line-height: ${fontSize.medium};
  `,
  container: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
};

OrganizationFeatureEntitlements.propTypes = {
  id: PropTypes.string.isRequired,
};

export default OrganizationFeatureEntitlements;
